/**
 * Personal-profile service -- the shared Profile page's ONE API surface.
 *
 * Card: "Personal Profile page" -- https://trello.com/c/BxskXBad
 * Backend: api/src/endpoints/me_profile.py
 *
 * Every call here is self-scoped to the authenticated caller: there is no
 * account-id argument on ANY function, mirroring the backend where no
 * route takes an account id. That is deliberate -- it means no caller of
 * this module can accidentally construct a cross-tenant request, because
 * there is no parameter through which to express one.
 *
 * Used by ProfileSettings.vue, which mounts inside all four app chromes
 * (SuperAdmin, Distributor, Client Portal, Storefront).
 */
import { apiCall } from './api'

/** Read the signed-in user's own identity fields. */
export function getMyProfile() {
  return apiCall('get', '/me/profile')
}

/**
 * Update the signed-in user's own name and/or username.
 *
 * `fields` accepts only first_name / last_name / username -- the backend
 * rejects anything else (including `email`, which is verification-gated
 * and goes through requestEmailChange below).
 */
export function updateMyProfile(fields) {
  return apiCall('patch', '/me/profile', fields)
}

/**
 * Live "is this username taken" probe for the Profile form.
 *
 * UX ONLY. The authoritative guard is the server-side uniqueness check
 * performed on save -- a probe answer is stale the moment it returns, and
 * two users can both be told "available" for the same name. Never treat
 * a true here as permission to skip handling a save-time rejection.
 */
export function checkUsernameAvailable(username) {
  return apiCall('get', '/me/username-available', { params: { username } })
}

/**
 * Change the signed-in user's password.
 *
 * `currentPassword` is required by the server, not merely by the form --
 * a stolen-but-valid session token must not be upgradeable into
 * permanent account ownership.
 *
 * `signOutOtherDevices` defaults to false. When true the server revokes
 * this account's OTHER sessions and keeps the caller's own.
 */
export function changeMyPassword(
  currentPassword,
  newPassword,
  signOutOtherDevices = false
) {
  return apiCall('post', '/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
    sign_out_other_devices: signOutOtherDevices,
  })
}

/**
 * REQUEST an email change. Does NOT change the email.
 *
 * The server stages the request and mails a confirm link to the NEW
 * address; the account email only moves once that link is followed. The
 * current address is notified too. Callers must therefore present this
 * as "check your email", never as a completed change.
 */
export function requestEmailChange(newEmail, currentPassword) {
  return apiCall('post', '/me/email', {
    new_email: newEmail,
    current_password: currentPassword,
  })
}

/** Redeem a confirm token from the emailed link (unauthenticated route). */
export function confirmEmailChange(token) {
  return apiCall('post', `/me/email/confirm/${encodeURIComponent(token)}`)
}
